import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { skipInDemoMode, withDemoModeFallback } from '../base/demoModeUtils';
import {
  toProjectAccessRecord,
  buildAccessUserDetails,
} from './projectNormalisers';

export async function addUserToProject(
  userId: string,
  project_id: string,
  accessLevel: 'read' | 'write' | 'admin' | 'record'
): Promise<void> {
  try {
    const projectAccessRef = collection(db, 'ProjectAccess');
    const q = query(
      projectAccessRef,
      where('userId', '==', userId),
      where('project_id', '==', project_id)
    );
    const existingAccess = await getDocs(q);

    if (!existingAccess.empty) {
      return;
    }

    const projectRef = doc(db, 'Projects', project_id);
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) {
      console.error(`[ProjectManager] Project ${project_id} does not exist`);
      return;
    }

    await addDoc(projectAccessRef, {
      project_id,
      userId,
      accessLevel,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error(`[ProjectManager] Error adding user ${userId} to project ${project_id}:`, error);
    throw error;
  }
}

export function listenForProjectAccessChanges(
  userId: string,
  callback: () => void
): () => void {
  const projectAccessRef = collection(db, 'ProjectAccess');
  const q = query(projectAccessRef, where('userId', '==', userId));

  return onSnapshot(
    q,
    () => {
      callback();
    },
    (error) => {
      console.error('ProjectManager.listenForProjectAccessChanges: Error in listener:', error);
    }
  );
}

export async function addGroupToProject(
  project_id: string,
  groupId: string,
  accessLevel: 'read' | 'record'
): Promise<void> {
  try {
    const projectAccessRef = collection(db, 'ProjectAccess');
    await addDoc(projectAccessRef, {
      project_id,
      groupId,
      accessLevel,
    });
  } catch (error) {
    console.error(`Error adding group ${groupId} to project ${project_id}:`, error);
    throw error;
  }
}

export async function removeUserFromProject(
  userId: string,
  project_id: string
): Promise<void> {
  await skipInDemoMode(async () => {
    try {
      const projectAccessRef = collection(db, 'ProjectAccess');
      const q = query(
        projectAccessRef,
        where('userId', '==', userId),
        where('project_id', '==', project_id)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return;
      }

      const batch = writeBatch(db);
      snapshot.docs.forEach((accessDoc) => {
        batch.delete(accessDoc.ref);
      });
      await batch.commit();
    } catch (error) {
      console.error(
        `[ProjectManager] Error removing user ${userId} from project ${project_id}:`,
        error
      );
      throw error;
    }
  });
}

export async function getProjectAccessUsers(project_id: string): Promise<any[]> {
  return await withDemoModeFallback(
    async () => [],
    async () => {
      try {
        const projectAccessRef = collection(db, 'ProjectAccess');
        const q = query(projectAccessRef, where('project_id', '==', project_id));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          return [];
        }

        const accessRecords = snapshot.docs.map((accessDoc) =>
          toProjectAccessRecord(accessDoc.id, accessDoc.data())
        );

        const userDetailsPromises = accessRecords.map(async (record) => {
          try {
            const userRef = doc(db, 'Users', record.userId);
            const userDoc = await getDoc(userRef);

            return {
              ...record,
              userDetails: buildAccessUserDetails(
                record.userId,
                userDoc.exists() ? userDoc.data() : null
              ),
            };
          } catch (error) {
            console.error(
              `[ProjectManager] Error fetching user details for ${record.userId}:`,
              error
            );
            return {
              ...record,
              userDetails: {
                userName: 'Error Loading User',
                userKey: record.userId,
                email: '',
              },
            };
          }
        });

        return await Promise.all(userDetailsPromises);
      } catch (error) {
        console.error(
          `[ProjectManager] Error getting project access users for ${project_id}:`,
          error
        );
        throw error;
      }
    }
  );
}

export async function updateUserProjectAccess(
  userId: string,
  project_id: string,
  newAccessLevel: 'read' | 'write' | 'admin' | 'record'
): Promise<void> {
  await skipInDemoMode(async () => {
    try {
      const projectAccessRef = collection(db, 'ProjectAccess');
      const q = query(
        projectAccessRef,
        where('userId', '==', userId),
        where('project_id', '==', project_id)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return;
      }

      await Promise.all(
        snapshot.docs.map(async (accessDoc) => {
          await updateDoc(accessDoc.ref, { accessLevel: newAccessLevel });
          return accessDoc.id;
        })
      );
    } catch (error) {
      console.error(
        `[ProjectManager] Error updating user access for ${userId} in project ${project_id}:`,
        error
      );
      throw error;
    }
  });
}
